/**
 * 이상한 방 - 아이소메트릭 카메라 컨트롤러
 *
 * 근거 문서: `이상한방-상태모델-v0.1.md` 원칙 2(1절) · 3.1절 · 4.1절 · 11.3절
 *
 * ## 이 파일이 게임 루프다
 *
 * 아이소메트릭 전환으로 게임 루프가 다시 정의됐다:
 * **"줌아웃으로 전체를 훑다가 → 이상한 곳을 줌인해서 확인 → 탭으로 지목"**
 * 즉 **줌 레벨이 1인칭의 시야 차단을 대체한다.** 줌 범위 설계가 곧 난이도이고,
 * 회전 가능 여부가 곧 공정성이다. 이 두 가지가 이 파일의 존재 이유 전부다.
 *
 * ## 회전은 선택이 아니라 필수다
 *
 * 3.1절은 "가려진 위치는 허용한다 - 자유 카메라로 접근 가능하면 문제없다"고 못박는다.
 * 아이소메트릭에서 이 전제를 위협하는 것이 **고정 시점**이다. 한 각도에서 영원히 가려지는 물건은
 * 난이도가 아니라 **찾을 수 없는 정답**이고, 탄환 경제 하에서 억울한 탈락으로 직결된다.
 * 그래서 4방향 90° 회전은 미관 기능이 아니라 원칙 2의 집행 장치다.
 *
 * 회전을 **보간**하는 것도 같은 이유에 속한다. 즉시 스냅하면 "지금 어느 방향에서 보고 있는지"를
 * 잃어버려서, 방금 본 소파 뒤를 다시 찾지 못한다. 그러면 회전이 있어도 없는 것과 같다.
 *
 * 대신 보간에는 대가가 하나 붙는다 - **"목표 방향"과 "지금 보이는 방향"이 0.3초 동안 다르다.**
 * 그래서 방향을 묻는 창구가 둘이다(`getDirection` / `getVisualDirection`).
 * 화면에 딸린 것(컷어웨이)이 목표 방향을 보면 카메라가 돌기도 전에 벽이 걷혀
 * "있어야 할 벽이 없는" 0.3초가 생긴다.
 *
 * ## 줌 범위의 두 끝
 *
 * - 정규화 0 = 방 전체가 화면에 들어옴 (훑기)
 * - 정규화 1 = 화면 세로에 월드 2m가 담김 → 최소 소품(1셀 = 0.25m)이 화면 세로의 1/8
 *
 * 아래쪽 끝이 4.1절의 실질적 집행이다 - "1칸 이동 = 최소 소품 하나만큼 어긋남"을 보장해 놓고
 * 그 어긋남을 화면에서 볼 수 없으면 그리드 해상도 결정 자체가 무의미해진다.
 * 폰 화면(세로 800px 가정)에서 25cm 소품이 100px로 잡히므로 한 칸 어긋남이 눈에 보인다.
 *
 * 사이 구간은 **기하급수**로 잇는다. 줌 감각은 덧셈이 아니라 곱셈이라(2배씩 확대) 선형으로 이으면
 * 줌아웃 쪽은 아무 일도 안 일어나고 줌인 쪽만 급격해진다.
 *
 * ## 상태 변경 규약
 *
 * 이 파일은 Three.js 카메라를 제자리 변경한다. 렌더·입력 레이어 한정 예외이며(11.3절이
 * "클라이언트가 해도 되는 것: 렌더링, 카메라, 입력, 보간"이라 규정한 영역),
 * 게임 상태(`RoomState`)는 단 한 줄도 건드리지 않는다.
 *
 * ## 입력 이벤트는 여기 없다
 *
 * 포인터·터치 제스처는 `pointerInput.ts`가 소유하고 이 파일의 메서드를 부른다.
 * 둘을 합치면 "핀치를 어떻게 해석하는가"와 "카메라가 어떻게 움직이는가"가 한 덩어리가 되어
 * 어느 쪽도 단위 테스트할 수 없게 된다.
 */

import * as THREE from 'three'

import { smoothstep } from '../core/math'
import type { RoomBounds } from '../core/types'
import {
  ISO_AZIMUTH_BASE_RADIANS,
  ISO_DIRECTION_COUNT,
  ISO_QUARTER_TURN_RADIANS,
  isoCameraDistance,
  isoHalfExtent,
  isoOffsetDirection,
  isoProjectedHalfExtents,
  isoScreenRight,
  isoScreenUp,
  roomCenterWorld,
  roomWorldSize,
  type SceneCtx,
} from '../render/scene'

/* --- 조작 상수 ------------------------------------------------------------------ */

/**
 * 회전 보간 시간(초). 0.3은 "방향이 바뀐 것을 눈이 따라갈 수 있는" 하한에 가깝다.
 * 더 길면 1분짜리 페이즈에서 조작이 굼떠지고, 더 짧으면 스냅과 구별되지 않는다.
 *
 * 공개하는 이유는 **컷어웨이 페이드와 짝이기 때문이다.** 벽 교체는 회전의 중간에 시작하므로
 * (`getVisualDirection` 참고) 페이드가 이 값의 절반을 넘으면 회전이 끝난 뒤에도 벽이 덜 걷혀 있다.
 * 그 관계는 `isoCamera.test.ts`가 `CUTAWAY_FADE_SECONDS`와 함께 고정한다.
 */
export const ISO_ROTATE_DURATION_SECONDS = 0.3

/** `focusOn` 이동 시간(초). 리빌 연출·정답 방 복귀에 쓰이므로 회전보다 조금 여유 있게 잡는다. */
const FOCUS_DURATION_SECONDS = 0.45

/** 최대 줌에서 화면 세로에 담기는 월드 높이(미터). 파일 상단 "줌 범위의 두 끝" 참고. */
const CLOSEUP_VIEW_HEIGHT_METERS = 2

/**
 * 최대 줌아웃에서 방 둘레에 남기는 여백 배수.
 * 딱 맞게 두면 방 외벽이 화면 가장자리에 붙어 "잘린 것 같은" 인상을 주고,
 * 실제로 픽셀 반올림에 따라 한 줄이 잘리기도 한다.
 */
const ZOOM_OUT_MARGIN = 1.06

/**
 * 팬 관성의 감쇠 시정수(초). 0.12면 약 0.36초 만에 5%까지 줄어든다.
 * **모바일 조작감의 핵심이면서 동시에 멀미의 원인**이라 일부러 짧게 잡았다 -
 * 지도 앱처럼 길게 미끄러지면 "이상한 곳"을 지나쳐 버려 훑기가 성립하지 않는다.
 */
const PAN_INERTIA_TAU_SECONDS = 0.12

/** 이보다 느려지면 관성을 끊는다(m/s). 남겨 두면 영원히 0에 수렴만 하며 매 프레임 계산이 돈다. */
const PAN_INERTIA_MIN_SPEED = 0.02

/**
 * 관성 속도 상한(초당 화면 높이 배수).
 * 속도는 `이동량 / dt`로 추정하는데 dt가 아주 짧은 프레임에서는 이 값이 폭발한다.
 * 상한이 없으면 손가락을 뗀 순간 화면이 방 밖으로 날아간다.
 */
const MAX_PAN_SPEED_SCREENS_PER_SECOND = 3

/** 속도 추정에 쓰는 dt 하한(초). 0으로 나누는 것을 막는다. */
const MIN_ESTIMATE_SECONDS = 1 / 240

const NORMALIZED_ZOOM_MIN = 0
const NORMALIZED_ZOOM_MAX = 1
const HALF = 0.5
const FULL_TURN_RADIANS = Math.PI * 2

/* --- 타입 ----------------------------------------------------------------------- */

/** 4방향 회전 인덱스. 방위각 = 45° + dir × 90°. */
export type IsoDirection = 0 | 1 | 2 | 3

export type IsoCamera = {
  readonly camera: THREE.OrthographicCamera
  setDirection(dir: IsoDirection, animate?: boolean): void
  /**
   * **회전 목표** 방향. `setDirection`을 부른 즉시 바뀐다.
   * UI 상태(나침반 하이라이트·스냅샷 저장)처럼 "사용자가 뭘 눌렀는가"를 묻는 쪽이 이걸 쓴다.
   */
  getDirection(): IsoDirection
  /**
   * **카메라가 지금 실제로 서 있는** 방향. 보간 중에는 방위각의 중간점에서 갈린다.
   * 화면에 보이는 것을 따라가야 하는 쪽 - 무엇보다 컷어웨이(`render/cutaway.ts`)가 이걸 쓴다.
   * 근거는 아래 구현부 주석 참고.
   */
  getVisualDirection(): IsoDirection
  rotate(delta: 1 | -1): void
  /**
   * 정규화 줌(0 = 전체 조망, 1 = 최대 확대).
   *
   * `anchorX`/`anchorY`는 **캔버스 좌상단 기준 CSS 픽셀**이며, 주면 그 지점의 월드 좌표가
   * 제자리에 남도록 시점을 함께 옮긴다. 생략하면 화면 중앙 기준이다.
   * 이 선택 인자가 없으면 핀치·휠 줌이 전부 화면 중앙으로 빨려 들어가는데,
   * 그러면 "이상한 곳을 줌인해서 확인"이 매번 팬 조작을 동반하게 된다.
   */
  setZoom(z: number, anchorX?: number, anchorY?: number): void
  getZoom(): number
  panBy(dxPx: number, dyPx: number): void
  focusOn(worldPos: THREE.Vector3, animate?: boolean): void
  /**
   * 새 라운드의 첫 화면 - 기본 방향(0) · 방 중심 · 줌 0, 애니메이션 없이. 두 세션(`game/session.ts` · `game/matchSession.ts`)이
   * 외우기 진입에 부른다. 셋을 각 세션이 따로 적으면 한쪽만 방향을 남기는 날 두 판의 첫 화면이 다르다. 언제 부르는가(외우기 진입만,
   * 수색 진입에는 절대 아니다)는 부르는 쪽의 규약이고 그 이유도 거기 있다.
   */
  resetView(): void
  update(dt: number): void
  dispose(): void
}

/** 진행 중인 보간 하나. 회전과 focusOn이 같은 모양을 쓴다. */
type Tween = { elapsed: number; readonly duration: number }

/* --- 보조 --------------------------------------------------------------------- */

function azimuthOfDirection(dir: IsoDirection): number {
  return ISO_AZIMUTH_BASE_RADIANS + dir * ISO_QUARTER_TURN_RADIANS
}

/**
 * 방위각을 가장 가까운 4방향 인덱스로 접는다. **회전 보간 중에 화면이 어느 방향인지**를 묻는 답이다.
 *
 * ## 왜 진척도(0..1)가 아니라 방향을 돌려주는가
 *
 * 컷어웨이는 "지금 어느 두 벽을 걷을까"만 물으므로 받는 쪽이 필요한 것은 결국 방향 하나다.
 * 진척도를 넘기면 호출부(`main.ts`)가 "0.5를 넘었으면 목표, 아니면 이전 방향"이라는 판정을
 * 스스로 들고 있어야 하는데, 그러면 **직전 방향을 기억하는 상태**가 배선 코드에 생긴다.
 * 회전 도중 다시 회전하거나 `setDirection(..., false)`로 건너뛰는 경우까지 거기서 맞춰야 한다.
 * 방위각에서 직접 접으면 그 상태가 아예 없다 - 어떤 경로로 각도가 정해졌든 답이 하나로 떨어진다.
 *
 * ## 왜 하필 중간에서 갈리는가
 *
 * 45°+90°k 지점(= 회전의 정확히 중간)에서 교체 대상인 두 벽면은 시선과 평행해져
 * **화면 투영 면적이 0**이 된다. 그 순간에는 어느 쪽을 켜고 꺼도 화면이 변하지 않는다.
 * 즉 임의로 고른 임계값이 아니라 **벽 교체가 공짜인 유일한 순간**이다
 * (`cutaway.ts` 헤더의 백페이스 컬링 설명과 같은 사실이다).
 * `smoothstep(0.5) = 0.5`이므로 이 지점은 보간 시간의 중간과도 정확히 일치한다.
 */
function directionOfAzimuth(currentAzimuth: number): IsoDirection {
  const steps = (currentAzimuth - ISO_AZIMUTH_BASE_RADIANS) / ISO_QUARTER_TURN_RADIANS
  return normalizeDirection(Math.round(steps))
}

/** `dir`을 0..3 범위로 되돌린다. 음수 나머지가 나오지 않도록 한 번 더 더한다. */
function normalizeDirection(dir: number): IsoDirection {
  const wrapped = ((dir % ISO_DIRECTION_COUNT) + ISO_DIRECTION_COUNT) % ISO_DIRECTION_COUNT
  return wrapped as IsoDirection
}

/**
 * `to`와 같은 방향이면서 `from`에 가장 가까운 각도.
 * 이걸 거치지 않으면 3 → 0 회전이 270°짜리 역주행이 되어 화면이 통째로 한 바퀴 돈다.
 */
function nearestAngle(from: number, to: number): number {
  const raw = to - from + Math.PI
  const delta = ((raw % FULL_TURN_RADIANS) + FULL_TURN_RADIANS) % FULL_TURN_RADIANS - Math.PI
  return from + delta
}

/* --- 본체 ----------------------------------------------------------------------- */

/**
 * 아이소메트릭 카메라를 만든다. `ctx.camera`를 제자리에서 조작하며 새 카메라를 만들지 않는다 -
 * 카메라가 둘이면 레이캐스터(`picker.ts`·`tapPicker.ts`)가 어느 쪽을 쓰는지 알 수 없게 된다.
 *
 * 절두체는 `createScene`이 이미 방 크기에 맞춰 놨다고 가정한다.
 * 그 가정이 깨진 채로 돌면 줌 계산이 0으로 나누거나 음수 배율을 만들어 화면이 뒤집히는데,
 * 그건 조용히 이상해지는 종류의 실패라 생성 시점에 즉시 던진다.
 */
export function createIsoCamera(ctx: SceneCtx, bounds: RoomBounds): IsoCamera {
  const camera = ctx.camera
  assertConfiguredFrustum(camera)

  const size = roomWorldSize(bounds)
  const center = roomCenterWorld(bounds)
  const distance = isoCameraDistance(bounds)

  /**
   * 방이 화면에서 차지하는 크기. **4방향 어디서 봐도 같으므로 한 번만 계산한다**
   * (45°·135°·225°·315°에서 sin/cos 절댓값이 모두 √2/2로 같다).
   * 회전 보간 중간 각도는 이보다 좁게 투영되므로 이 값이 곧 최악의 경우다.
   */
  const roomExtents = isoProjectedHalfExtents(size, ISO_AZIMUTH_BASE_RADIANS)

  // 매 프레임 도는 경로이므로 프레임당 Vector3 할당을 만들지 않는다.
  const screenRight = new THREE.Vector3()
  const screenUp = new THREE.Vector3()
  const viewAxis = new THREE.Vector3()
  const scratch = new THREE.Vector3()

  /**
   * 화면 중앙이 바라보는 월드 좌표. **읽기 전용 파생값이며 `desired`에서 매 프레임 다시 계산된다.**
   * 카메라 배치에 쓰는 것은 이쪽이다.
   */
  const target = center.clone()

  /**
   * "어디를 보고 싶은가"(의도). 팬·줌 앵커·focusOn이 움직이는 것은 전부 이 점이고,
   * 실제 시점 `target`은 여기서 줌에 따라 접어서 만든다.
   *
   * 둘을 가른 이유는 **클램프가 파괴적이기 때문이다.** 줌아웃 상태에서는 방이 화면보다 작아
   * 허용 범위가 0이 되는데(아래 `clampTarget`), 그때 한 점으로 접어 버리면 의도가 사라진다.
   * 그러면 "줌아웃 상태에서 어딘가를 지정 → 줌인" 순서가 항상 방 중심으로 끌려간다 -
   * 리빌 연출·정답 방 복귀(`focusOn`)가 조용히 엉뚱한 곳을 비추게 되는 경로다.
   */
  const desired = center.clone()

  let direction: IsoDirection = 0
  let azimuth = azimuthOfDirection(direction)
  let spinFrom = azimuth
  let spinTo = azimuth
  let spin: Tween | null = null

  const focusFrom = new THREE.Vector3()
  const focusTo = new THREE.Vector3()
  let focus: Tween | null = null

  let zoomNormalized = NORMALIZED_ZOOM_MIN

  /** 이번 프레임에 팬으로 들어온 이동량(월드). 관성 속도 추정의 입력이다. */
  const pendingPan = new THREE.Vector3()
  const panVelocity = new THREE.Vector3()

  let disposed = false

  /* --- 화면 ↔ 월드 -------------------------------------------------------------- */

  function viewportSize(): { width: number; height: number } {
    const element = ctx.renderer.domElement
    return { width: element.clientWidth, height: element.clientHeight }
  }

  /**
   * CSS 픽셀 하나에 해당하는 월드 길이(미터).
   * `camera.zoom`이 분모에 있으므로 **줌인할수록 같은 드래그가 더 조금 움직인다** -
   * 이게 없으면 최대 확대 상태에서 손가락 한 번에 방 반대편으로 날아간다.
   *
   * 레이아웃 이전이라 캔버스 크기가 0이면 변환이 정의되지 않는다. 오류가 아니라 일시 상태이므로
   * 0을 돌려주고 호출부가 이번 입력을 버린다(`syncRendererToDisplaySize`의 0 크기 처리와 같은 규약).
   */
  function worldPerPixel(): number {
    const heightPx = viewportSize().height
    if (heightPx <= 0) return 0
    return (camera.top - camera.bottom) / camera.zoom / heightPx
  }

  /* --- 줌 ---------------------------------------------------------------------- */

  /**
   * 현재 절두체에서 쓸 수 있는 `camera.zoom`의 양 끝.
   *
   * 매 프레임 다시 계산하는 이유는 **리사이즈** 때문이다. 폰을 가로로 눕히면 절두체 가로가 넓어져
   * 방 전체가 더 큰 배율에서도 들어온다. 생성 시점에 굳혀 두면 회전 후 방이 잘리거나
   * 반대로 필요 이상으로 멀어진다.
   */
  function zoomRange(): { min: number; max: number } {
    const frustumHalfWidth = (camera.right - camera.left) * HALF
    const frustumHalfHeight = (camera.top - camera.bottom) * HALF

    const fit =
      Math.min(
        frustumHalfWidth / roomExtents.halfWidth,
        frustumHalfHeight / roomExtents.halfHeight,
      ) / ZOOM_OUT_MARGIN
    const closeup = frustumHalfHeight / (CLOSEUP_VIEW_HEIGHT_METERS * HALF)

    // 방이 아주 작으면 "전체 조망"이 이미 최대 확대보다 가까울 수 있다. 범위가 뒤집히면
    // 기하급수 보간이 음수 지수를 타므로 여기서 접어 둔다.
    return { min: fit, max: Math.max(closeup, fit) }
  }

  /** 정규화 줌 → `camera.zoom`. 기하급수인 이유는 파일 상단 "줌 범위의 두 끝" 참고. */
  function zoomFromNormalized(normalized: number): number {
    const range = zoomRange()
    return range.min * Math.pow(range.max / range.min, normalized)
  }

  /* --- 시점 클램프 -------------------------------------------------------------- */

  function refreshBasis(): void {
    isoScreenRight(azimuth, screenRight)
    isoScreenUp(azimuth, screenUp)
    isoOffsetDirection(azimuth, viewAxis)
  }

  /**
   * 시점이 방을 벗어나지 못하게 막는다.
   *
   * 화면 기저(오른쪽·위·시선축)로 분해해서 각 축을 따로 자른다. 월드 축으로 자르면
   * 화면에서는 대각선으로 걸리는 이상한 벽이 생긴다.
   *
   * 허용 범위 = **방의 투영 반지름 − 화면의 절반**이다. 이 뺄셈이 요점이다:
   * 줌아웃해서 방이 화면보다 작아지면 허용 범위가 0이 되어 시점이 방 중심에 고정된다.
   * 즉 "줌아웃하면 방 전체가 보인다"가 별도 코드 없이 성립한다.
   *
   * 주의: 이 규칙은 시점을 방 바운딩 박스 **안에** 가두지 않는다. 화면 위쪽 끝까지 팬하면
   * 시점의 월드 y는 천장보다 높아진다 - 그 지점이 화면에서는 방 안쪽(먼 바닥 구석)을 가리키기 때문이다.
   * 직교 투영에서 의미 있는 경계는 3D 위치가 아니라 **화면에 방 아닌 곳만 남지 않는가**이고,
   * 그것을 지키는 것이 위 뺄셈이다.
   *
   * 클램프는 **두 단계**다. 먼저 의도(`desired`)를 방 크기 안으로만 묶고(줌과 무관),
   * 그 다음 실제 시점(`target`)을 줌에 따른 범위로 접는다. 한 단계로 합치면 줌아웃 중에
   * 의도가 지워져서, 줌인했을 때 보고 있던 곳으로 돌아가지 못한다(`desired` 주석 참고).
   */
  function clampTarget(): void {
    refreshBasis()

    const roomRight = isoHalfExtent(size, screenRight)
    const roomUp = isoHalfExtent(size, screenUp)
    // 시선축은 직교 투영에서 화면에 아무 영향이 없다. 그래도 방 밖으로 나가면
    // 근·원평면 여유(scene.ts)를 갉아먹으므로 방 바운딩 박스 안에 묶어 둔다.
    const roomView = isoHalfExtent(size, viewAxis)

    // 1단계 - 의도를 방 안으로. 줌과 무관하므로 줌아웃해도 지워지지 않는다.
    scratch.copy(desired).sub(center)
    const wantRight = THREE.MathUtils.clamp(scratch.dot(screenRight), -roomRight, roomRight)
    const wantUp = THREE.MathUtils.clamp(scratch.dot(screenUp), -roomUp, roomUp)
    const wantView = THREE.MathUtils.clamp(scratch.dot(viewAxis), -roomView, roomView)

    desired
      .copy(center)
      .addScaledVector(screenRight, wantRight)
      .addScaledVector(screenUp, wantUp)
      .addScaledVector(viewAxis, wantView)

    // 2단계 - 실제 시점. 줌아웃해서 방이 화면보다 작아지면 범위가 0이 되어 방 중심에 고정된다.
    const visibleHalfWidth = (camera.right - camera.left) * HALF / camera.zoom
    const visibleHalfHeight = (camera.top - camera.bottom) * HALF / camera.zoom
    const limitRight = Math.max(0, roomRight - visibleHalfWidth)
    const limitUp = Math.max(0, roomUp - visibleHalfHeight)

    target
      .copy(center)
      .addScaledVector(screenRight, THREE.MathUtils.clamp(wantRight, -limitRight, limitRight))
      .addScaledVector(screenUp, THREE.MathUtils.clamp(wantUp, -limitUp, limitUp))
      .addScaledVector(viewAxis, wantView)
  }

  /** 줌·시점을 실제 카메라에 반영한다. 조작 메서드는 전부 마지막에 이걸 부른다. */
  function applyPose(): void {
    const nextZoom = zoomFromNormalized(zoomNormalized)
    if (camera.zoom !== nextZoom) {
      camera.zoom = nextZoom
      camera.updateProjectionMatrix()
    }

    clampTarget()

    camera.position.copy(target).addScaledVector(viewAxis, distance)
    camera.lookAt(target)
    camera.updateMatrixWorld()
  }

  /* --- 프레임 진행 -------------------------------------------------------------- */

  /** 보간 하나를 진행시키고 0..1 진척도를 돌려준다. 끝났으면 정확히 1이다. */
  function advance(tween: Tween, dt: number): number {
    tween.elapsed += dt
    return Math.min(tween.elapsed / tween.duration, 1)
  }

  function advanceSpin(dt: number): void {
    if (spin === null) return

    const progress = advance(spin, dt)
    azimuth = spinFrom + (spinTo - spinFrom) * smoothstep(progress)

    if (progress < 1) return
    // 부동소수점 누적이 각도를 조금씩 갉아먹으면 "정확히 90°"가 무너진다. 끝에서 목표값으로 굳힌다.
    azimuth = spinTo
    spin = null
  }

  function advanceFocus(dt: number): void {
    if (focus === null) return

    const progress = advance(focus, dt)
    desired.lerpVectors(focusFrom, focusTo, smoothstep(progress))

    if (progress < 1) return
    desired.copy(focusTo)
    focus = null
  }

  /**
   * 팬 관성.
   *
   * 이번 프레임에 드래그 입력이 있었으면 속도만 갱신하고(끄는 동안은 손가락이 진실이다),
   * 없었으면 남은 속도로 미끄러지며 지수 감쇠한다.
   *
   * 이동량을 `속도 × dt`가 아니라 **감쇠를 적분한 `속도 × τ × (1 − e^(−dt/τ))`**로 계산한다.
   * 전자는 프레임이 길수록 과대적분해서, 총 미끄러짐 거리가 프레임률에 따라 달라진다 -
   * 60fps 폰과 30fps 폰에서 같은 플릭이 다른 거리를 가면 조작감을 튜닝할 수 없다.
   * 이 식에서는 총 거리가 정확히 `속도 × τ`로 프레임률과 무관하게 고정된다.
   */
  function advancePan(dt: number): void {
    if (pendingPan.lengthSq() > 0) {
      panVelocity.copy(pendingPan).divideScalar(Math.max(dt, MIN_ESTIMATE_SECONDS))
      limitPanSpeed()
      pendingPan.set(0, 0, 0)
      return
    }

    if (panVelocity.lengthSq() === 0) return

    const remaining = Math.exp(-dt / PAN_INERTIA_TAU_SECONDS)
    desired.addScaledVector(panVelocity, PAN_INERTIA_TAU_SECONDS * (1 - remaining))
    panVelocity.multiplyScalar(remaining)
    if (panVelocity.length() < PAN_INERTIA_MIN_SPEED) panVelocity.set(0, 0, 0)
  }

  function limitPanSpeed(): void {
    const visibleHeight = (camera.top - camera.bottom) / camera.zoom
    const maxSpeed = visibleHeight * MAX_PAN_SPEED_SCREENS_PER_SECOND
    if (panVelocity.length() > maxSpeed) panVelocity.setLength(maxSpeed)
  }

  /* --- 공개 조작 ---------------------------------------------------------------- */

  function setDirection(dir: IsoDirection, animate = true): void {
    if (disposed) return

    const next = normalizeDirection(dir)
    if (next === direction && spin === null) return

    direction = next
    const goal = nearestAngle(azimuth, azimuthOfDirection(next))
    // 회전 중에 팬 관성이 남아 있으면 화면이 두 방향으로 동시에 미끄러져 방향 감각을 잃는다.
    panVelocity.set(0, 0, 0)

    if (!animate) {
      spin = null
      azimuth = goal
      applyPose()
      return
    }

    spinFrom = azimuth
    spinTo = goal
    spin = { elapsed: 0, duration: ISO_ROTATE_DURATION_SECONDS }
  }

  /**
   * 줌 앵커 보정. 확대 전후로 `anchor` 지점의 월드 좌표가 같은 픽셀에 남도록 시점을 옮긴다.
   *
   * 화면 오프셋(px) × 픽셀당 월드 길이 = 시점에서 앵커까지의 월드 오프셋이므로,
   * 픽셀당 길이가 변한 만큼만 시점을 밀면 된다. 화면 y는 아래로 증가하므로 위 벡터에는 부호를 뒤집는다.
   */
  function anchorZoom(anchorX: number, anchorY: number, before: number, after: number): void {
    const viewport = viewportSize()
    if (viewport.width <= 0 || viewport.height <= 0) return

    const frustumHeight = camera.top - camera.bottom
    const shift = frustumHeight / viewport.height * (1 / before - 1 / after)

    refreshBasis()
    desired
      .addScaledVector(screenRight, (anchorX - viewport.width * HALF) * shift)
      .addScaledVector(screenUp, -(anchorY - viewport.height * HALF) * shift)
  }

  function setZoom(z: number, anchorX?: number, anchorY?: number): void {
    if (disposed) return

    const next = THREE.MathUtils.clamp(z, NORMALIZED_ZOOM_MIN, NORMALIZED_ZOOM_MAX)
    if (next === zoomNormalized) return

    const before = camera.zoom
    zoomNormalized = next
    const after = zoomFromNormalized(zoomNormalized)

    camera.zoom = after
    camera.updateProjectionMatrix()

    if (anchorX !== undefined && anchorY !== undefined) {
      anchorZoom(anchorX, anchorY, before, after)
    }

    applyPose()
  }

  function panBy(dxPx: number, dyPx: number): void {
    if (disposed) return

    const scale = worldPerPixel()
    if (scale === 0) return

    refreshBasis()
    // 오른쪽으로 끌면 월드가 오른쪽으로 따라와야 하므로 시점은 왼쪽으로 간다.
    // 아래로 끌면(화면 y 증가) 시점은 화면 위쪽으로 간다.
    scratch
      .set(0, 0, 0)
      .addScaledVector(screenRight, -dxPx * scale)
      .addScaledVector(screenUp, dyPx * scale)

    desired.add(scratch)
    pendingPan.add(scratch)
    // 드래그는 한 프레임에 여러 번 들어올 수 있고, 그 사이에도 카메라가 실제로 움직여 있어야 한다.
    // 다음 `update`까지 미루면 손가락과 화면이 한 프레임씩 어긋난다.
    applyPose()
  }

  function focusOn(worldPos: THREE.Vector3, animate = true): void {
    if (disposed) return

    // 관성이 살아 있으면 도착 직후 다시 미끄러져 연출이 흔들린다.
    panVelocity.set(0, 0, 0)
    pendingPan.set(0, 0, 0)

    if (!animate) {
      focus = null
      desired.copy(worldPos)
      applyPose()
      return
    }

    focusFrom.copy(desired)
    focusTo.copy(worldPos)
    focus = { elapsed: 0, duration: FOCUS_DURATION_SECONDS }
  }

  // 만들자마자 화면이 맞아야 한다. 첫 `update` 전에 렌더가 한 번 도는 호출부가 흔하다.
  applyPose()

  return {
    camera,
    setDirection,
    getDirection: () => direction,
    getVisualDirection: () => directionOfAzimuth(azimuth),
    rotate: (delta) => setDirection(normalizeDirection(direction + delta), true),
    setZoom,
    getZoom: () => zoomNormalized,
    panBy,
    focusOn,

    resetView(): void {
      setDirection(0, false)
      focusOn(center, false)
      setZoom(0)
    },

    update(dt: number): void {
      if (disposed) return

      advanceSpin(dt)
      advanceFocus(dt)
      // 팬 관성은 focusOn 보간보다 뒤에 둔다 - 연출 중에는 관성이 이미 0이라 순서가 무의미하고,
      // 그 외에는 이번 프레임 드래그가 보간보다 우선해야 손가락이 화면에 붙어 있는 느낌이 난다.
      advancePan(dt)

      applyPose()
    },

    /**
     * 이 컨트롤러는 이벤트 리스너를 소유하지 않는다(`pointerInput.ts`의 몫).
     * 그래도 dispose를 두는 이유는 **수명 관리**다 - 모드 전환에서 낡은 컨트롤러 참조가 남아
     * 새 컨트롤러와 같은 카메라를 매 프레임 두 번 덮어쓰면 화면이 진동한다.
     * 해제 후에는 모든 조작이 조용히 무시된다.
     */
    dispose(): void {
      disposed = true
      spin = null
      focus = null
      panVelocity.set(0, 0, 0)
      pendingPan.set(0, 0, 0)
    },
  }
}

/**
 * 절두체가 세워져 있는지 확인한다.
 * `createScene`을 거치지 않고 직접 조립한 컨텍스트에서만 걸리는 조건이며,
 * 통과시키면 0으로 나눈 배율이 화면을 뒤집거나 비워 버린다 - 원인 추적이 불가능한 종류의 고장이다.
 */
function assertConfiguredFrustum(camera: THREE.OrthographicCamera): void {
  if (camera.top > camera.bottom && camera.right > camera.left) return

  throw new RangeError(
    '직교 절두체가 세워져 있지 않다. `createScene`(또는 `configureIsoCamera`)을 먼저 거쳐야 한다: ' +
      `left=${camera.left}, right=${camera.right}, top=${camera.top}, bottom=${camera.bottom}`,
  )
}
